// [APP-FORK] 앱(웹뷰) 여부 판별 — 런타임 UA 감지.
//   shopuser 도메인은 앱 웹뷰와 웹 브라우저 사용자가 하나의 번들을 함께 쓴다
//   (prod = shopuser.naegift.com. dev/qa 도 shopuser-dev/-qa.naegift.com 으로 통합 예정 —
//    기존 앱 전용 도메인에서 '-app' 만 빠진 형태다).
//   빌드타임 env(REACT_APP_IS_APP)는 하나의 번들에 하나의 값뿐이라 둘을 런타임에 구분할 수 없다.
//   =true 로 두면 웹 사용자도 isApp()=true → Login.js:227 · GlobalLogin.js:40 이 null 을 반환해
//   로그인 폼이 사라지는데, Main.tsx:61-63 의 네이티브 위임은 브릿지가 없어 skip 된다 → 데드엔드.
//   근거: 앱 웹뷰가 UA 에 'naegiftApp/1.0' 을 명시 설정한다
//         (naegift_mobile_app/lib/webview/web_view_page.dart:133-137 InAppWebViewSettings.userAgent).
//         Android 는 UA 리터럴에 마커를 직접 포함하고, iOS 는 같은 파일 :91 에서 기본 WKWebView UA
//         끝에 ' naegiftApp/1.0' 을 덧붙인다(커밋 4ab6972). :112-116 이 UA 조회 완료 전엔 웹뷰를
//         만들지 않으므로 마커 없는 로드는 발생하지 않는다.
//   ★user 웹뷰는 admin 웹뷰와 같은 단일 InAppWebView 다 — app_shell.dart:783-784 가 유일한
//     WebViewPage 이고 :229-231 _navigateTo 가 같은 컨트롤러에 admin URL 을 로드한다.
//     따라서 위 마커는 user 화면에도 항상 붙는다.
//   ★되돌리지 말 것 — naegift-admin 이 같은 이유로 env 전환(990bc4f)을 UA 로 복귀시켰다(271c333).
//     (naegift-admin-app/src/utils/isApp.js 의 동명 함수와 같은 구현 — 의도적 복제)
//   ★window.flutter_inappwebview 마커를 쓰지 않는 이유: 주입 타이밍이 보장되지 않는데
//     아래 :99-101 리시버 등록이 "모듈 평가 시점"에 실행돼, 마커가 아직 없으면 등록이
//     통째로 스킵된다. navigator.userAgent 는 스크립트 실행 시점에 항상 존재 → 타이밍 안전.

export const isApp = (): boolean =>
  typeof navigator !== 'undefined' &&
  navigator.userAgent.includes('naegiftApp');

// [APP-FORK] 모바일 브라우저 판정 — AppOnlyGuard 의 앱 유도 대상. 태블릿은 제외한다
//  (iPad 는 iPadOS 가 데스크톱 UA 를 보내 자연히 빠진다). storeUrls.ts 의 OS 판정과 목적이 다르다.
export const isMobile = (): boolean =>
  typeof navigator !== 'undefined' &&
  /android|iphone|ipod|windows phone/i.test(navigator.userAgent || '');

/** [APP-FORK] MainHeader 숨김 시 마켓 페이지 상단 여백도 제거 */
export const isMarketHeaderOffsetEnabled = (): boolean => !isApp();

/**
 * [APP-FORK] 세션 만료 등으로 진입하는 로그인 경로만 (OAuth 콜백 제외)
 * - /login
 * - /login/:storeNo
 */
export const isAppSessionLoginRoute = (pathname: string): boolean =>
  pathname === '/login' || /^\/login\/\d+$/.test(pathname);

/**
 * [APP-FORK] 네이티브(flutter_inappwebview)에 통보할 수 있는 환경인가.
 *   ★isApp()과 기준이 다르다: isApp()(UA)은 "우리 앱 웹뷰가 이 문서를 열었는가"를 말할 뿐,
 *     브릿지가 실제로 주입됐는지는 보장하지 않는다(UA 는 문자열, 브릿지 주입은 웹뷰 초기화 타이밍).
 *     이 함수는 callHandler 를 부를 수 있는지를 직접 확인한다.
 *   → "서버는 처리됐는데 네이티브는 모르는" 상태가 생기는 흐름에선 반드시 이 함수로 판정한다.
 *     (앱 탈퇴: 서버는 동의를 철회했는데 네이티브 로컬 동의 플래그가 남아, 재로그인 시
 *      약관을 다시 보지 않고 동의가 재전송된다 — MyInfo.tsx handleAppWithdraw 참고)
 *   Main.tsx:63 requestRelogin 처럼 실패해도 무해한 통보엔 isApp() + optional chaining 으로 충분.
 */
export const canNotifyNative = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.flutter_inappwebview !== 'undefined';

// [APP-FORK] 네이티브 브릿지 타입 (flutter_inappwebview)
declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (name: string, ...args: unknown[]) => Promise<unknown>;
    };
    // [APP-FORK] 앱 토큰 수신 리시버 (네이티브가 호출; appAuthReceiver.ts 에서 등록)
    //   provider 는 kakao/naver/google/apple 4종 (리시버는 분기 없이 그대로 백엔드 전달)
    //   email 은 애플 전용, 최초 로그인에만 값이 실린다.
    onNaegiftAuthToken?: (payload: {
      provider: 'kakao' | 'naver' | 'google' | 'apple';
      token: string;
      email?: string;
    }) => void;
    // [APP-FORK] 앱 로그아웃 리시버 (네이티브가 호출; appAuthReceiver.ts 에서 등록)
    //   ★Promise 반환 — 네이티브가 서버 로그아웃 완료를 await 할 수 있게.
    onNaegiftLogout?: () => Promise<void>;
    // [APP-FORK] 앱 로그인취소 리시버 (네이티브 로그인 시트 닫힘 시 호출; appAuthReceiver.ts 에서 등록)
    //   ★/login(isAppSessionLoginRoute) 일 때만 복귀 — 아니면 no-op(웹은 이미 원래 페이지에 있다).
    //   복귀 시: redirect(globalLoginTarget) 있으면 마켓홈('/'), 없으면 history.back().
    onNaegiftLoginCancel?: () => void;
    // [APP-FORK] 앱 세션 확인 리시버 (네이티브가 앱 시작 시 호출; appAuthReceiver.ts 에서 등록)
    //   ★Promise<boolean> 반환 — /profile 쿠키 호출로 로그인 여부(200/401) 판정.
    onNaegiftCheckSession?: () => Promise<boolean>;
    // [APP-FORK] FCM 토큰 갱신 리시버 (네이티브가 호출; appAuthReceiver.ts 에서 등록)
    onNaegiftFcmToken?: (token: string) => Promise<void>;
  }
}

// [APP-FORK] 앱 토큰 수신 리시버 등록(앱 웹뷰에서만 로드 — UA 에 naegiftApp 마커가 있을 때).
// Main.tsx 가 이미 이 모듈을 import 하므로 앱 시작 시 이 블록이 실행된다.
// 웹 브라우저·카카오 인앱브라우저(isApp=false)는 등록 안 됨 → 웹 영향 0.
if (isApp()) {
  void import('./appAuthReceiver').then(m => m.initAppAuthReceiver());
}

export {};
